import { transform } from 'ol/proj';

import type { ExportFormat, SourceDef } from './types';
import { effectiveDef, findTime, resolutionOf } from './catalogue';
import { SJTSK } from './projections';
import { makeZip } from './zip';

/**
 * Export at the source's own pixel size, in S-JTSK / Krovak (EPSG:5514).
 *
 * The point is a 1:1 crop: we ask the service for the region in its native CRS at
 * its native resolution, so no reprojection or rescaling happens anywhere in the
 * chain. The output is therefore aligned to the Krovak grid — on a north-up screen
 * it looks rotated by the meridian convergence, which is correct, not a bug.
 *
 * Verified against the services on 2026-09-29: WMS 1.1.1 and 1.3.0 both take
 * EPSG:5514 bounds easting-first (the swapped order returns empty imagery), and
 * ArcGIS export/exportImage accept bboxSR/imageSR=5514 directly.
 */

/**
 * Above this the export is still allowed — it is your machine — but it is worth
 * saying so first: the canvas holds 4 bytes a pixel while encoding, and browsers
 * have their own ceilings (Chrome refuses a canvas over ~268 Mpx, mobile Safari
 * far less). Past those the encode fails rather than producing a bad file.
 */
export const WARN_PIXELS = 60_000_000;

const CHUNK_PX = 2048;
const CONCURRENCY = 3;

export interface ExportChunk {
  x: number;
  y: number;
  width: number;
  height: number;
  bbox: [number, number, number, number];
}

export interface ExportPlan {
  def: SourceDef;
  timeId?: string;
  res: number;
  bbox: [number, number, number, number];
  width: number;
  height: number;
  chunks: ExportChunk[];
  groundWidth: number;
  groundHeight: number;
  /** Big enough to be worth a caution — not a refusal. */
  huge: boolean;
}

export function isExportable(def: SourceDef, timeId?: string): boolean {
  return Boolean(effectiveDef(def, timeId).export);
}

/** Native pixel size: the epoch's own value wins, since archives vary by year. */
export function exportResolution(def: SourceDef, timeId?: string): number {
  const eff = effectiveDef(def, timeId);
  return findTime(def, timeId ?? '')?.res ?? eff.export?.res ?? resolutionOf(def, timeId) ?? 1;
}

function snap(value: number, res: number, origin: number, mode: 'floor' | 'ceil'): number {
  const steps = (value - origin) / res;
  return origin + (mode === 'floor' ? Math.floor(steps) : Math.ceil(steps)) * res;
}

/**
 * The drawn rectangle is axis-aligned on screen (Web Mercator); its Krovak bounding
 * box is therefore slightly larger and rotated. We snap outward to the source's
 * pixel grid so every output pixel maps to one source pixel.
 */
export function planExport(parent: SourceDef, timeId: string | undefined, extent3857: number[]): ExportPlan {
  const def = effectiveDef(parent, timeId);
  const res = exportResolution(parent, timeId);
  const [minX, minY, maxX, maxY] = extent3857;
  const corners: number[][] = [
    [minX, minY],
    [maxX, minY],
    [minX, maxY],
    [maxX, maxY],
  ].map((c) => transform(c, 'EPSG:3857', SJTSK));

  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  const origin = def.export?.origin ?? [0, 0];

  const bbox: [number, number, number, number] = [
    snap(Math.min(...xs), res, origin[0], 'floor'),
    snap(Math.min(...ys), res, origin[1], 'floor'),
    snap(Math.max(...xs), res, origin[0], 'ceil'),
    snap(Math.max(...ys), res, origin[1], 'ceil'),
  ];

  const width = Math.max(1, Math.round((bbox[2] - bbox[0]) / res));
  const height = Math.max(1, Math.round((bbox[3] - bbox[1]) / res));

  const maxChunk = Math.min(def.export?.maxPx ?? CHUNK_PX, CHUNK_PX);
  const chunks: ExportChunk[] = [];
  for (let y = 0; y < height; y += maxChunk) {
    for (let x = 0; x < width; x += maxChunk) {
      const w = Math.min(maxChunk, width - x);
      const h = Math.min(maxChunk, height - y);
      chunks.push({
        x,
        y,
        width: w,
        height: h,
        bbox: [
          bbox[0] + x * res,
          // Canvas y grows downward, Krovak northing grows upward.
          bbox[3] - (y + h) * res,
          bbox[0] + (x + w) * res,
          bbox[3] - y * res,
        ],
      });
    }
  }

  return {
    def,
    timeId,
    res,
    bbox,
    width,
    height,
    chunks,
    groundWidth: bbox[2] - bbox[0],
    groundHeight: bbox[3] - bbox[1],
    huge: width * height > WARN_PIXELS,
  };
}

function chunkUrl(plan: ExportPlan, chunk: ExportChunk, format: ExportFormat): string {
  const spec = plan.def.export!;
  const { timeId, def } = plan;
  const bbox = chunk.bbox.join(',');
  const imageFormat = format === 'png' ? 'png' : 'jpg';

  if (spec.via === 'wms') {
    const layers = (spec.layers ?? def.wms?.layers ?? '').replaceAll('{time}', timeId ?? '');
    const q = new URLSearchParams({
      SERVICE: 'WMS',
      // 1.1.1 keeps the bounds easting-first with no axis-order ambiguity.
      VERSION: '1.1.1',
      REQUEST: 'GetMap',
      LAYERS: layers,
      STYLES: '',
      SRS: SJTSK,
      BBOX: bbox,
      WIDTH: String(chunk.width),
      HEIGHT: String(chunk.height),
      FORMAT: format === 'png' ? 'image/png' : 'image/jpeg',
    });
    const url = spec.url;
    return `${url}${url.includes('?') ? '&' : '?'}${q}`;
  }

  const q = new URLSearchParams({
    bbox,
    bboxSR: '5514',
    imageSR: '5514',
    size: `${chunk.width},${chunk.height}`,
    format: imageFormat,
    f: 'image',
  });
  if (spec.via === 'arcgis') {
    const layers = (spec.layers ?? def.arcgis?.layers ?? '').replaceAll('{time}', timeId ?? '');
    if (layers) q.set('layers', layers);
  }
  return `${spec.url}?${q}`;
}

export interface ExportProgress {
  done: number;
  total: number;
}

export async function runExport(
  plan: ExportPlan,
  format: ExportFormat,
  onProgress: (p: ExportProgress) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nepodařilo se vytvořit plátno pro export.');

  let done = 0;
  const queue = [...plan.chunks];

  async function worker(): Promise<void> {
    for (;;) {
      const chunk = queue.shift();
      if (!chunk) return;
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const res = await fetch(chunkUrl(plan, chunk, format), { signal, mode: 'cors' });
      if (!res.ok) throw new Error(`Služba odpověděla ${res.status} (dlaždice ${chunk.x}/${chunk.y}).`);
      const bitmap = await createImageBitmap(await res.blob());
      ctx!.drawImage(bitmap, chunk.x, chunk.y);
      bitmap.close();
      onProgress({ done: ++done, total: plan.chunks.length });
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, plan.chunks.length) }, worker));

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, format === 'png' ? 'image/png' : 'image/jpeg', 0.92),
  );
  if (!blob) throw new Error('Nepodařilo se zakódovat obrázek.');
  // Free the backing store now rather than waiting for GC; these are large.
  canvas.width = 0;
  canvas.height = 0;
  return blob;
}

/** ESRI WKT for EPSG:5514, so GIS software reads the .prj without a lookup. */
const PRJ_5514 =
  'PROJCS["S-JTSK / Krovak East North",GEOGCS["S-JTSK",DATUM["System_Jednotne_Trigonometricke_Site_Katastralni",' +
  'SPHEROID["Bessel 1841",6377397.155,299.1528128,AUTHORITY["EPSG","7004"]],TOWGS84[589,76,480,0,0,0,0],' +
  'AUTHORITY["EPSG","6156"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],' +
  'UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4156"]],PROJECTION["Krovak"],' +
  'PARAMETER["latitude_of_center",49.5],PARAMETER["longitude_of_center",24.83333333333333],' +
  'PARAMETER["azimuth",30.28813972222222],PARAMETER["pseudo_standard_parallel_1",78.5],' +
  'PARAMETER["scale_factor",0.9999],PARAMETER["false_easting",0],PARAMETER["false_northing",0],' +
  'UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["X",EAST],AXIS["Y",NORTH],AUTHORITY["EPSG","5514"]]';

/**
 * World file: pixel size, no rotation terms (the raster is already axis-aligned in
 * Krovak), then the centre of the top-left pixel.
 */
function worldFile(plan: ExportPlan): string {
  return [
    plan.res,
    0,
    0,
    -plan.res,
    plan.bbox[0] + plan.res / 2,
    plan.bbox[3] - plan.res / 2,
  ]
    .map((n) => n.toFixed(10))
    .join('\n') + '\n';
}

export function exportName(plan: ExportPlan): string {
  const epoch = plan.timeId ? `_${findTime(plan.def, plan.timeId)?.label ?? plan.timeId}` : '';
  return (
    `${plan.def.id}${epoch}_5514`
      // Fold diacritics rather than blanking them: "nejnovější" should become
      // "nejnovejsi", not "nejnov-j-".
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^\w.-]+/g, '-')
  );
}

export function packageExport(plan: ExportPlan, image: Blob, format: ExportFormat): Promise<Blob> {
  const base = exportName(plan);
  const ext = format === 'png' ? 'png' : 'jpg';
  const worldExt = format === 'png' ? 'pgw' : 'jgw';
  const encoder = new TextEncoder();
  const lic = plan.def.attribution;

  const readme =
    `${plan.def.title}${plan.timeId ? ` — ${findTime(plan.def, plan.timeId)?.label ?? plan.timeId}` : ''}\n` +
    `Zdroj: ${plan.def.provider}\n` +
    `Atribuce: ${lic}\n\n` +
    `Souřadnicový systém: S-JTSK / Krovak East North (EPSG:5514)\n` +
    `Velikost pixelu: ${plan.res} m\n` +
    `Rozměr: ${plan.width} x ${plan.height} px (${plan.groundWidth.toFixed(1)} x ${plan.groundHeight.toFixed(1)} m)\n` +
    `Rozsah (5514): ${plan.bbox.join(', ')}\n\n` +
    `Obrázek je v nativním rozlišení zdroje a v jeho vlastním souřadnicovém systému,\n` +
    `takže nedošlo k žádnému převzorkování. Vůči severu je proto pootočen.\n` +
    `World file (.${worldExt}) a .prj přiloženy — stačí otevřít v QGIS.\n`;

  return image.arrayBuffer().then((buf) =>
    makeZip([
      { name: `${base}.${ext}`, data: new Uint8Array(buf) },
      { name: `${base}.${worldExt}`, data: encoder.encode(worldFile(plan)) },
      { name: `${base}.prj`, data: encoder.encode(PRJ_5514) },
      { name: 'README.txt', data: encoder.encode(readme) },
    ]),
  );
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
